import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Trophy, Clock, User, Medal, Timer, ExternalLink, Sparkles } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import type { Result, Event, SplitPoint, SplitTime } from '../types';
import { supabase } from '../lib/supabase';
import { DEFAULT_CERTIFICATE_SETTINGS, SYSTEM_FONTS } from '../constants/certificates';
import ClubResults from './ClubResults';
import FinishOverview from './FinishOverview';

interface Props {
  result: Result;
  event: Event;
  onClose: () => void;
}

const PREVIEW_SAMPLE = {
  name: 'John Runner',
  rank: 42,
  category_position: 12,
  chip_time: '3:45:22'
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
  });
};

export default function ResultModal({ result, event, onClose }: Props) {
  const [splitPoints, setSplitPoints] = useState<SplitPoint[]>([]);
  const [splitTimes, setSplitTimes] = useState<SplitTime[]>([]);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClubResults, setShowClubResults] = useState(false);
  const [clubResults, setClubResults] = useState<Result[]>([]);
  const [loadingClubResults, setLoadingClubResults] = useState(false);
  const [nearbyResults, setNearbyResults] = useState<Result[]>([]);
  const [selectedResult, setSelectedResult] = useState<Result | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);
  const [aiSummaryFetched, setAiSummaryFetched] = useState(false);
  const [aiSummaryVisible, setAiSummaryVisible] = useState(false);
  const [aiSummaryRevealProgress, setAiSummaryRevealProgress] = useState(0);
  const [requestId, setRequestId] = useState<string | null>(null);
  
  // Only show course map if route_map_url is not null (meaning it's visible)
  const courseMap = event?.route_map_url || null;
  const defaultMapUrl = "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&q=80&w=1000";
  
  const certificateSettings = {
    ...DEFAULT_CERTIFICATE_SETTINGS,
    ...event?.certificate_settings
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const summaryContainerRef = useRef<HTMLDivElement>(null);
  const aiSummaryRequestRef = useRef<AbortController | null>(null);

  // Determine if right column should be shown
  const shouldShowRightColumn = Boolean(
    courseMap || 
    (splitPoints.length > 0) || 
    (event.show_finish_overview && nearbyResults.length > 0)
  );

  // Prevent click propagation
  const handleModalClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Load split data
  const loadSplitData = useCallback(async () => {
    if (!event?.id || !result?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      const [pointsResponse, timesResponse] = await Promise.all([
        supabase
          .from('split_points')
          .select('*')
          .eq('event_id', event.id)
          .order('order_index', { ascending: true }),
        supabase
          .from('split_times')
          .select('*')
          .eq('result_id', result.id)
      ]);

      if (pointsResponse.error) throw pointsResponse.error;
      if (timesResponse.error) throw timesResponse.error;

      setSplitPoints(pointsResponse.data || []);
      setSplitTimes(timesResponse.data || []);
    } catch (err: any) {
      console.error('Error loading split data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [event?.id, result?.id]);

  useEffect(() => {
    loadSplitData();
  }, [loadSplitData]);

  // Reset AI summary state when result or event changes
  useEffect(() => {
    // Abort any in-progress request
    if (aiSummaryRequestRef.current) {
      aiSummaryRequestRef.current.abort();
      aiSummaryRequestRef.current = null;
    }
    
    setAiSummary(null);
    setAiSummaryError(null);
    setAiSummaryLoading(false);
    setAiSummaryFetched(false);
    setAiSummaryVisible(false);
    setAiSummaryRevealProgress(0);
    setRequestId(null);
  }, [result.id, event.id]);

  // Load AI summary if enabled
  useEffect(() => {
    const fetchAiSummary = async () => {
      // Skip if already fetched or not enabled
      if (!event.enable_ai_summary || aiSummaryFetched) return;

      // Abort any in-progress request
      if (aiSummaryRequestRef.current) {
        aiSummaryRequestRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      aiSummaryRequestRef.current = abortController;

      setAiSummaryLoading(true);
      setAiSummaryError(null);
      setAiSummary(null);

      // Generate a unique request ID for tracking
      const reqId = uuidv4();
      setRequestId(reqId);
      console.log(`[AI Summary] Starting request ${reqId} for result ${result.id}`);

      try {
        const response = await fetch(`${supabase.supabaseUrl}/functions/v1/generate-result-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabase.supabaseKey}`,
            'X-Request-ID': reqId
          },
          body: JSON.stringify({
            result,
            event,
            splitTimes,
            splitPoints,
            requestId: reqId
          }),
          signal: abortController.signal
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate AI summary');
        }

        const data = await response.json();
        console.log(`[AI Summary] Received response for request ${reqId}`);
        
        // Set summary but don't show it yet
        setAiSummary(data.summary);
        setAiSummaryFetched(true);
        
        // Delay showing the summary to allow for fade-in animation
        setTimeout(() => {
          setAiSummaryVisible(true);
          
          // Start the reveal animation
          const duration = 1500; // 1.5 seconds for the full reveal
          const startTime = Date.now();
          
          const animateReveal = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            setAiSummaryRevealProgress(progress);
            
            if (progress < 1) {
              requestAnimationFrame(animateReveal);
            }
          };
          
          requestAnimationFrame(animateReveal);
        }, 1000); // 1 second delay before showing
      } catch (err: any) {
        // Don't set error if it was an abort
        if (err.name !== 'AbortError') {
          console.error(`[AI Summary] Error for request ${reqId}:`, err);
          setAiSummaryError(err.message || 'Could not generate summary.');
          setAiSummaryFetched(true); // Mark as fetched even on error to prevent retries
        }
      } finally {
        // Keep loading state until animation completes
        setTimeout(() => {
          if (abortController === aiSummaryRequestRef.current) {
            setAiSummaryLoading(false);
            console.log(`[AI Summary] Request ${reqId} completed`);
            aiSummaryRequestRef.current = null;
          }
        }, 1000);
      }
    };

    // Only fetch if AI summary is enabled and we haven't fetched yet
    if (event.enable_ai_summary && !aiSummaryFetched && !aiSummaryLoading) {
      fetchAiSummary();
    }
  }, [event.enable_ai_summary, result, event, splitTimes, splitPoints, aiSummaryFetched, aiSummaryLoading]);

  // Load club results when showing club results modal
  useEffect(() => {
    if (showClubResults && result.club) {
      const loadClubResults = async () => {
        try {
          setLoadingClubResults(true);
          const { data, error } = await supabase
            .from('results')
            .select('*')
            .eq('event_id', event.id)
            .eq('club', result.club)
            .order('club_position', { ascending: true });

          if (error) throw error;
          setClubResults(data || []);
        } catch (err) {
          console.error('Error loading club results:', err);
        } finally {
          setLoadingClubResults(false);
        }
      };

      loadClubResults();
    }
  }, [showClubResults, result.club, event.id]);

  // Load nearby results
  useEffect(() => {
    const loadNearbyResults = async () => {
      if (!result.rank) return;
      
      try {
        const { data, error } = await supabase
          .from('results')
          .select('*')
          .eq('event_id', event.id)
          .gte('rank', Math.max(1, result.rank - 3))
          .lte('rank', result.rank + 3)
          .order('rank', { ascending: true });

        if (error) throw error;
        setNearbyResults(data || []);
      } catch (err) {
        console.error('Error loading nearby results:', err);
      }
    };

    loadNearbyResults();
  }, [event.id, result.rank]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Convert HH:MM:SS time string to seconds
  const timeToSeconds = (time: string): number => {
    if (!time || time === 'DNF') return 0;
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Convert seconds to HH:MM:SS format
  const secondsToTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate cumulative time at each split point
  const getCumulativeTime = (splitIndex: number): string => {
    if (!splitTimes || splitTimes.length === 0) return '-';

    let totalSeconds = 0;
    for (let i = 0; i <= splitIndex; i++) {
      const splitTime = splitTimes.find(st => st.split_point_id === splitPoints[i].id);
      if (!splitTime || splitTime.split_time === 'DNF') return 'DNF';
      totalSeconds += timeToSeconds(splitTime.split_time);
    }
    return secondsToTime(totalSeconds);
  };

  // Calculate pace for a given cumulative time and distance
  const calculatePace = (time: string, distance: number) => {
    if (!time || time === 'DNF' || !distance) return null;
    const totalSeconds = timeToSeconds(time);
    
    if (event.show_speed_instead_of_pace) {
      // Calculate speed in km/h
      const hours = totalSeconds / 3600;
      const speed = distance / hours;
      return `${speed.toFixed(1)} km/h`;
    } else {
      // Calculate pace in min/km
      const paceSeconds = totalSeconds / distance;
      const paceMinutes = Math.floor(paceSeconds / 60);
      const remainingSeconds = Math.round(paceSeconds % 60);
      return `${paceMinutes}:${remainingSeconds.toString().padStart(2, '0')} /km`;
    }
  };

  const averagePace = calculatePace(result.chip_time, event.distance || 0);

  const generateCertificate = async () => {
    setGeneratingCertificate(true);

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d')!;
      canvas.width = 1200;
      canvas.height = 800;

      // Background color
      ctx.fillStyle = certificateSettings.backgroundColor;
      ctx.fillRect(0, 0, 1200, 800);

      // Background image if set
      if (certificateSettings.background?.url) {
        try {
          const bgImage = await loadImage(certificateSettings.background.url);
          const bgScale = Math.max(
            1200 / bgImage.width,
            800 / bgImage.height
          );
          const width = bgImage.width * bgScale;
          const height = bgImage.height * bgScale;
          const x = (1200 - width) / 2;
          const y = (800 - height) / 2;

          ctx.globalAlpha = certificateSettings.background.opacity;
          ctx.drawImage(bgImage, x, y, width, height);
          ctx.globalAlpha = 1;
        } catch (err) {
          console.warn('Failed to load background image:', err);
        }
      }

      // Logo
      let logoHeight = 0;
      if (certificateSettings.logo) {
        try {
          const logo = await loadImage(certificateSettings.logo);
          const logoWidth = 240;
          logoHeight = (logo.height / logo.width) * logoWidth;
          ctx.drawImage(logo, (1200 - logoWidth) / 2, 60, logoWidth, logoHeight);
        } catch (err) {
          console.warn('Failed to load logo:', err);
        }
      }

      // Title
      ctx.fillStyle = certificateSettings.textColor;
      const titleY = logoHeight ? 160 + logoHeight : 200;

      ctx.font = `bold 52px ${SYSTEM_FONTS[certificateSettings.font]}`;
      ctx.textAlign = 'center';
      ctx.fillStyle = `${certificateSettings.textColor}10`;
      ctx.fillText(certificateSettings.titleText, 1200 / 2 + 2, titleY + 2);
      ctx.fillStyle = certificateSettings.textColor;
      ctx.fillText(certificateSettings.titleText, 1200 / 2, titleY);

      // Event name
      ctx.font = `500 40px ${SYSTEM_FONTS[certificateSettings.font]}`;
      const eventNameY = titleY + 80;
      ctx.fillText(event.name, 1200 / 2, eventNameY);

      // Date
      ctx.font = `400 28px ${SYSTEM_FONTS[certificateSettings.font]}`;
      const dateY = eventNameY + 50;
      ctx.fillText(new Date(event.date).toLocaleDateString(), 1200 / 2, dateY);

      // "Presented to" text
      ctx.font = `italic 28px ${SYSTEM_FONTS[certificateSettings.font]}`;
      ctx.fillText('Presented to', 1200 / 2, dateY + 80);

      // Participant name
      ctx.font = `600 48px ${SYSTEM_FONTS[certificateSettings.font]}`;
      const nameY = dateY + 140;
      ctx.fillStyle = `${certificateSettings.textColor}10`;
      ctx.fillText(result.name, 1200 / 2 + 2, nameY + 2);
      ctx.fillStyle = certificateSettings.textColor;
      ctx.fillText(result.name, 1200 / 2, nameY);

      // Achievement details
      const detailsY = nameY + 100;
      const detailsSpacing = 240;
      const detailHeight = 80;
      const detailWidth = 200;

      [-1, 0, 1].forEach((position, index) => {
        const x = 1200 / 2 + position * detailsSpacing - detailWidth / 2;
        const y = detailsY;
        
        // Card background with gradient
        const gradient = ctx.createLinearGradient(x, y, x, y + detailHeight);
        gradient.addColorStop(0, `${certificateSettings.accentColor}08`);
        gradient.addColorStop(1, `${certificateSettings.accentColor}15`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, y, detailWidth, detailHeight, 12);
        ctx.fill();

        // Card content
        ctx.fillStyle = certificateSettings.textColor;
        ctx.font = `400 24px ${SYSTEM_FONTS[certificateSettings.font]}`;
        ctx.textAlign = 'center';
        
        const details = [
          { label: 'Official Time', value: result.chip_time },
          { label: 'Overall Position', value: `#${result.rank}` },
          { label: 'Category Position', value: `#${result.category_position}` }
        ][index];
        
        ctx.fillText(details.label, x + detailWidth / 2, y + 35);
        ctx.font = `600 24px ${SYSTEM_FONTS[certificateSettings.font]}`;
        ctx.fillText(details.value, x + detailWidth / 2, y + 65);
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_certificate.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      console.error('Error generating certificate:', err);
    } finally {
      setGeneratingCertificate(false);
    }
  };

  if (!result || !event) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-0 sm:p-4 z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-none sm:rounded-xl shadow-2xl w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] ${shouldShowRightColumn ? '' : 'sm:max-w-2xl'} overflow-hidden relative flex flex-col`}
        onClick={handleModalClick}
      >
        {/* Hidden canvas for certificate generation */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Sticky Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Result Details</h2>
            <p className="text-sm sm:text-base text-gray-600">{event.name} · {new Date(event.date).toLocaleDateString()}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Two Column Layout */}
        <div className="flex-1 overflow-y-auto">
          <div className={`grid grid-cols-1 ${shouldShowRightColumn ? 'lg:grid-cols-2' : ''} gap-4 sm:gap-6 p-4 sm:p-6`}>
            {/* Left Column */}
            <div className="space-y-4 sm:space-y-6">
              {/* AI Summary Section */}
              {event.enable_ai_summary && (
                <div 
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    aiSummaryVisible || aiSummaryLoading ? 'max-h-[500px] opacity-100 mb-4 sm:mb-6' : 'max-h-0 opacity-0 m-0'
                  }`}
                  ref={summaryContainerRef}
                >
                  <div className="text-sm text-black-900 bg-gray-50 p-4 sm:p-6 rounded-xl border border-gray-200 relative min-h-[100px]">
                    {aiSummaryLoading && !aiSummaryVisible && (
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="flex space-x-2 mb-3">
                          <div className="w-2 h-2 bg-black rounded-full animate-[bounce_1.4s_ease-in-out_0s_infinite]"></div>
                          <div className="w-2 h-2 bg-black rounded-full animate-[bounce_1.4s_ease-in-out_0.2s_infinite]"></div>
                          <div className="w-2 h-2 bg-black rounded-full animate-[bounce_1.4s_ease-in-out_0.4s_infinite]"></div>
                        </div>
                        <span className="text-gray-600 font-medium">Analysis Loading</span>
                      </div>
                    )}
                    
                    {aiSummary && aiSummaryVisible && (
                      <div className="relative overflow-hidden">
                        <p ref={summaryRef} className="relative z-10">
                          {aiSummary}
                        </p>
                        <div 
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-50/80 to-gray-50"
                          style={{ 
                            transform: `translateX(${aiSummaryRevealProgress * 100}%)`,
                            transition: 'transform 0.1s ease-out'
                          }}
                        ></div>
                      </div>
                    )}
                    
                    {aiSummaryError && !aiSummaryLoading && (
                      <p className="text-red-600">
                        {aiSummaryError}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Chip Time */}
              <div className="bg-black text-white p-4 sm:p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5" />
                  <h3 className="text-lg">{event.show_gun_time_instead_of_chip_time ? 'Gun Time' : 'Chip Time'}</h3>
                </div>
                <p className="text-4xl sm:text-5xl font-bold tracking-wider">
                  {event.show_gun_time_instead_of_chip_time ? (result.gun_time || result.chip_time || 'DNF') : (result.chip_time || 'DNF')}
                </p>
                {(event.show_gun_time_instead_of_chip_time ? result.chip_time : result.gun_time) && (
                  <div className="mt-3 text-gray-400">
                    <span className="text-sm">{event.show_gun_time_instead_of_chip_time ? 'Chip Time' : 'Gun Time'}: </span>
                    <span className="font-medium">{event.show_gun_time_instead_of_chip_time ? result.chip_time : result.gun_time}</span>
                  </div>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Trophy className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-medium">Overall</h3>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold">#{result.rank || '-'}</p>
                </div>

                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Medal className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-medium">Category</h3>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold">#{result.category_position || '-'}</p>
                </div>
              </div>

              {/* Participant Details */}
              <div className="border border-gray-200 p-4 sm:p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <User className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-medium">Participant Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Name</p>
                    <p className="font-medium">{result.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Bib Number</p>
                    <p className="font-medium">{result.bib_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Category</p>
                    <p className="font-medium">{result.category || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Gender</p>
                    <p className="font-medium">{result.gender || '-'}</p>
                  </div>
                  {result.club && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Club</p>
                      <button
                        onClick={() => setShowClubResults(true)}
                        className="text-left font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2"
                      >
                        {result.club}
                        {result.club_position && (
                          <span className="text-gray-500">
                            (#{result.club_position})
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {certificateSettings.showOnResults && (
                  <button
                    onClick={generateCertificate}
                    disabled={generatingCertificate}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-md hover:bg-black disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    {generatingCertificate ? 'Generating...' : 'Download Certificate'}
                  </button>
                )}
                {event.action_button?.url && (
                  <a
                    href={event.action_button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-md hover:bg-black"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {event.action_button.text}
                  </a>
                )}
              </div>
            </div>

            {/* Right Column - Only render if needed */}
            {shouldShowRightColumn && (
              <div className="space-y-4 sm:space-y-6">
                {/* Split Times */}
                {splitPoints.length > 0 && (
                  <div className="border border-gray-200 p-4 sm:p-6 rounded-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <Timer className="w-5 h-5 text-gray-700" />
                      <h3 className="text-lg font-medium">
                        Split Times & {event.show_speed_instead_of_pace ? 'Speed' : 'Pacing'}
                      </h3>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">
                        Average {event.show_speed_instead_of_pace ? 'Speed' : 'Pace'}
                      </p>
                      <p className="text-2xl font-bold">{averagePace || '-'}</p>
                    </div>

                    <div className="space-y-3">
                      {splitPoints.map((point, index) => {
                        const cumulativeTime = getCumulativeTime(index);
                        const pace = calculatePace(cumulativeTime, point.distance);
                        
                        return (
                          <div key={point.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{point.name}</p>
                                <p className="text-sm text-gray-500">{point.distance}km</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">{cumulativeTime}</p>
                                <p className="text-sm text-gray-500">{pace || '-'}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Finish</p>
                            <p className="text-sm text-gray-500">{event.distance}km</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{result.chip_time}</p>
                            <p className="text-sm text-gray-500">{averagePace || '-'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Course Map - Only show if route_map_url is not null */}
                {courseMap && (
                  <div className="rounded-xl">
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={courseMap}
                        alt="Course Map"
                        className="w-full h-auto object-contain"
                        onError={(e) => {
                          e.currentTarget.src = defaultMapUrl;
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Finish Overview */}
                {event.show_finish_overview && nearbyResults.length > 0 && (
                  <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <Trophy className="w-5 h-5 text-gray-700" />
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium">Finish Overview</h3>
                        <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          BETA
                        </span>
                      </div>
                    </div>
                    <FinishOverview
                      currentResult={result}
                      nearbyResults={nearbyResults}
                      onResultClick={setSelectedResult}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Club Results Modal */}
        {showClubResults && result.club && (
          <ClubResults
            eventId={event.id}
            clubName={result.club}
            results={clubResults}
            onClose={() => setShowClubResults(false)}
            loading={loadingClubResults}
          />
        )}

        {/* Selected Result Modal */}
        {selectedResult && (
          <ResultModal
            result={selectedResult}
            event={event}
            onClose={() => setSelectedResult(null)}
          />
        )}
      </div>
    </div>
  );
}